import { Filter as FilterIcon, Globe, Landmark, Package } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type {
  AgentReportingEntry,
  AgentReportingStatus,
} from "../../components/charts/AgentReportingMatrix";
import { AgentReportingMatrix } from "../../components/charts/AgentReportingMatrix";
import type { RankedBarDatum } from "../../components/ChartsComponent";
import {
  ChartCard,
  DonutPieChart,
  RankedBarChart
} from "../../components/ChartsComponent";
import { DataTable } from "../../components/DataTable";
import { PageShell } from "../../components/layout/PageShell";
import { Modal } from "../../components/Modal";
import { TableToolbar } from "../../components/TableToolbar";
import { SegmentedTabs } from "../../components/SegmentedTabs";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { BlurredSheetContent } from "../itCompliance/components/BlurredSheetContent";
import { DrilldownSheetHeader } from "../itCompliance/components/DrilldownSheetHeader";
import { FilterDropdown } from "../itCompliance/components/FilterDropdown";
import { fetchScanDashboardFilterOptions } from "../ScanCoverage/services/scanCoverageApi";
import { TOOLBAR_BUTTON_CLASS } from "../itCompliance/constants/ui";
import { ScopeTreeFilter } from "../itCompliance/components/ScopeTreeFilter";
import { useAccessibleOrganizationsTree } from "../itCompliance/hooks";
import type { ScopeNode } from "../itCompliance/types";
import { scopeTriggerLabel, selectedScopeApplicationNames } from "../itCompliance/utils";
import { useCmdbToolOptions } from "./useCmdbToolOptions";
import type { ToolDetailRow } from "./assetTable";
import {
  AgentStatus,
  getAgentHealthAssetColumns,
  getAgentHealthSortPayload,
  TOOL_DETAIL_COLUMNS,
} from "./assetTable";
import type { FreshnessItem } from "./DataFreshness";
import {
  buildFreshnessSlices,
  DataFreshnessCard,
} from "./DataFreshness";
import type {
  AgentHealthAssetRow,
  AgentHealthAssetsRequest,
  AgentHealthCoverageRow,
  AgentHealthFilterOptions,
  AgentHealthFreshnessSummary,
  AgentHealthStatusMatrixRow,
  AgentHealthSummary,
} from "./services";
import {
  fetchAgentHealthAssets,
  fetchAgentHealthAssetTools,
  fetchAgentHealthCoverage,
  fetchAgentHealthFilterOptions,
  fetchAgentHealthFreshnessList,
  fetchAgentHealthFreshnessSummary,
  fetchAgentHealthStatusMatrix,
  fetchAgentHealthSummary,
} from "./services";
import { HEX, TILE_CLASS } from "./shared";

// Derived from the (filtered) roster so tiles and the status drilldown never disagree on counts.
const STATUS_COLORS: Record<AgentStatus, string> = {
  Active: HEX.healthy,
  Inactive: HEX.stale,
  Unreachable: HEX.critical,
  "Never Reported": HEX.neutral,
};
type StatusSliceItem = { name: AgentStatus; value: number; color: string };

// Each application x environment pairing wears its worst state (Never Reported > Unreachable >
// Inactive > Active); pairings with zero assets are omitted rather than defaulting to "active".
const ASSET_STATUS_BY_MATRIX: Record<AgentReportingStatus, AgentStatus> = {
  active: "Active",
  inactive: "Inactive",
  unreachable: "Unreachable",
  never: "Never Reported",
};

// Flattens the tree so the Application filter can start with everything selected.
function collectAllScopeIds(nodes: ScopeNode[]): string[] {
  const ids: string[] = [];
  const walk = (list: ScopeNode[]) => {
    for (const node of list) {
      ids.push(node.id);
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  return ids;
}

// Debounces the roster's POST /agent-health/assets fetch so typing doesn't fire a request per keystroke.
const ASSETS_FETCH_DEBOUNCE_MS = 300;

// Mirrors Log Review's LOG_REVIEW_DONUT_PROPS field for field so both donuts read identically.
const AGENT_STATUS_DONUT_PROPS = {
  nameKey: "name",
  valueKey: "value",
  colorKey: "color",
  variant: "donut",
  height: 280,
  showLegend: true,
  legendPosition: "top",
  legendToggle: true,
  showLegendShare: false,
  showSliceLabels: true,
  labelStyle: "leader",
  showCenterTotal: true,
  centerLabel: "Total",
  sort: "none",
} as const;

// ── Pure Helpers ──────────────────────────────────────────────────────────

/** Cycles sort key and direction across 3 states: asc -> desc -> none. */
function cycleSort3State(
  currentKey: string | null,
  currentDir: "asc" | "desc",
  nextKey: string,
): { sortKey: string | null; sortDir: "asc" | "desc" } {
  if (currentKey !== nextKey) {
    return { sortKey: nextKey, sortDir: "asc" };
  }
  if (currentDir === "asc") {
    return { sortKey: nextKey, sortDir: "desc" };
  }
  return { sortKey: null, sortDir: "asc" };
}

/** Everything selected is sent as empty (= no restriction). */
/** Drawer options are listed A-Z. */
const sortedOptions = (opts: string[]): string[] => [...opts].sort((a, b) => a.localeCompare(b));

function unlessAll(selected: string[], all: string[]): string[] {
  if (all.length > 0 && selected.length >= all.length) {
    return [];
  }
  return selected;
}

/** For the roster only, a drawer selection replaces the page-level one; the page-level filter seeds the drawer when applied. */
function narrow(global: string[], table: string[]): string[] {
  return table.length > 0 ? table : global;
}

function sameSelection(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v) => b.includes(v));
}

function getCoverageViewLabel(view: "application" | "organization" | "environment"): string {
  switch (view) {
    case "application":
      return "Application";
    case "organization":
      return "Organization";
    case "environment":
      return "Environment";
  }
}

interface DrilldownState {
  statusDrilldown: AgentStatus | "Total" | null;
  appDrilldown: string | null;
  orgDrilldown: string | null;
  envDrilldown: string | null;
  matrixDrilldown: {
    application: string;
    environment: string;
    status: AgentStatus;
  } | null;
}

interface DrilldownFilters {
  applications: string[];
  tools: string[];
  environments: string[];
  types: string[];
  agentStatuses: string[];
}

const EMPTY_DRILL_FILTERS: DrilldownFilters = {
  applications: [],
  tools: [],
  environments: [],
  types: [],
  agentStatuses: [],
};

/** Filters a drilldown already pins by its own scope, so the Filters drawer leaves them out. */
function getDrilldownHiddenFilters(state: DrilldownState): ("agentStatus" | "application" | "environment")[] {
  if (state.matrixDrilldown) return ["agentStatus", "application", "environment"];
  if (state.statusDrilldown) return ["agentStatus"];
  if (state.appDrilldown) return ["application"];
  if (state.envDrilldown) return ["environment"];
  return [];
}

function resolveDrilldownScope(state: DrilldownState): Partial<AgentHealthAssetsRequest> | null {
  if (state.statusDrilldown) {
    return { status: state.statusDrilldown === "Total" ? undefined : state.statusDrilldown };
  }
  if (state.appDrilldown) {
    return { unhealthyOnly: true, groupBy: "application", groupValue: state.appDrilldown };
  }
  if (state.orgDrilldown) {
    return { unhealthyOnly: true, groupBy: "organization", groupValue: state.orgDrilldown };
  }
  if (state.envDrilldown) {
    return { unhealthyOnly: true, groupBy: "environment", groupValue: state.envDrilldown };
  }
  if (state.matrixDrilldown) {
    return {
      matrixApplication: state.matrixDrilldown.application,
      matrixEnvironment: state.matrixDrilldown.environment,
      matrixStatus: state.matrixDrilldown.status,
    };
  }
  return null;
}

function getDrilldownHeaderText(state: DrilldownState): { title: string; slice: string | null } {
  if (state.matrixDrilldown) {
    return {
      title: "Agent Coverage by Application & Environment",
      slice: `${state.matrixDrilldown.application} · ${state.matrixDrilldown.environment} · ${state.matrixDrilldown.status}`,
    };
  }
  if (state.appDrilldown) {
    return {
      title: "Unhealthy Agents by Application",
      slice: `${state.appDrilldown} · Unhealthy`,
    };
  }
  if (state.orgDrilldown) {
    return {
      title: "Unhealthy Agents by Organization",
      slice: `${state.orgDrilldown} · Unhealthy`,
    };
  }
  if (state.envDrilldown) {
    return {
      title: "Unhealthy Agents by Environment",
      slice: `${state.envDrilldown} · Unhealthy`,
    };
  }
  return {
    title: "Agent Status Breakdown",
    slice: state.statusDrilldown,
  };
}

// ── Subcomponents ─────────────────────────────────────────────────────────

interface AgentHealthMoreFiltersSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filterOptions: AgentHealthFilterOptions;
  filterOptionsLoading: boolean;
  applicationOptions: string[];
  tools: string[];
  draftEnvironment: string[];
  setDraftEnvironment: (val: string[]) => void;
  draftType: string[];
  setDraftType: (val: string[]) => void;
  draftAgentStatus: string[];
  setDraftAgentStatus: (val: string[]) => void;
  draftTableApps: string[];
  setDraftTableApps: (val: string[]) => void;
  draftTableTools: string[];
  setDraftTableTools: (val: string[]) => void;
  onReset: () => void;
  onClearAndClose: () => void;
  onApply: () => void;
  /** Filters the surrounding view already pins (e.g. a status drilldown pins Agent Status). */
  hide?: ("agentStatus" | "application" | "environment")[];
}

function AgentHealthMoreFiltersSheet({
  open,
  onOpenChange,
  filterOptions,
  filterOptionsLoading,
  applicationOptions,
  tools,
  draftEnvironment,
  setDraftEnvironment,
  draftType,
  setDraftType,
  draftAgentStatus,
  setDraftAgentStatus,
  draftTableApps,
  setDraftTableApps,
  draftTableTools,
  setDraftTableTools,
  onReset,
  onClearAndClose,
  onApply,
  hide = [],
}: AgentHealthMoreFiltersSheetProps) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <BlurredSheetContent
        side="right"
        overlayClassName="bg-transparent backdrop-blur-none"
        className="flex h-full max-h-screen w-[min(50vw,42rem)] flex-col gap-0 border-l border-border bg-card p-0 max-sm:w-full"
      >
        <SheetHeader className="gap-1 border-b border-border px-3 py-2">
          <SheetTitle className="text-base font-semibold">Select Filters</SheetTitle>
        </SheetHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 flex flex-col gap-5">
          <div className="flex items-center justify-end">
            <Button
              type="button"
              variant="none"
              size="none"
              onClick={onReset}
              className="text-sm font-semibold text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
            >
              Reset Filters
            </Button>
          </div>
          {!hide.includes("agentStatus") && (
            <FilterDropdown
              label="Agent Status"
              options={sortedOptions(filterOptions.agentStatuses)}
              value={draftAgentStatus}
              onChange={setDraftAgentStatus}
              searchPlaceholder="Search"
              loading={filterOptionsLoading}
              disabled={filterOptionsLoading || filterOptions.agentStatuses.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
          )}
          {!hide.includes("application") && (
            <FilterDropdown
              label="Application"
              options={sortedOptions(applicationOptions)}
              value={draftTableApps}
              onChange={setDraftTableApps}
              searchPlaceholder="Search"
              disabled={applicationOptions.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
          )}
          {!hide.includes("environment") && (
            <FilterDropdown
              label="Environment"
              options={sortedOptions(filterOptions.environments)}
              value={draftEnvironment}
              onChange={setDraftEnvironment}
              searchPlaceholder="Search"
              loading={filterOptionsLoading}
              disabled={filterOptionsLoading || filterOptions.environments.length === 0}
              size="sm"
              surface="input"
              variant="pill"
              className="w-full px-4"
              modal
            />
          )}
          <FilterDropdown
            label="Tool"
            options={sortedOptions(tools)}
            value={draftTableTools}
            onChange={setDraftTableTools}
            searchPlaceholder="Search"
            disabled={tools.length === 0}
            size="sm"
            surface="input"
            variant="pill"
            className="w-full px-4"
            modal
          />
          <FilterDropdown
            label="Type"
            options={sortedOptions(filterOptions.types)}
            value={draftType}
            onChange={setDraftType}
            searchPlaceholder="Search"
            loading={filterOptionsLoading}
            disabled={filterOptionsLoading || filterOptions.types.length === 0}
            size="sm"
            surface="input"
            variant="pill"
            className="w-full px-4"
            modal
          />
        </div>
        <SheetFooter className="border-t border-border flex-row justify-between">
          <Button
            type="button"
            variant="none"
            size="none"
            onClick={onClearAndClose}
            className="px-3 py-1.5 text-sm font-medium rounded-md border border-border hover:bg-accent/60"
          >
            Clear and Close
          </Button>
          <Button
            type="button"
            variant="none"
            size="none"
            onClick={onApply}
            className="px-3 py-1.5 text-sm font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-40"
          >
            Apply Filters
          </Button>
        </SheetFooter>
      </BlurredSheetContent>
    </Sheet>
  );
}

interface AgentHealthDrilldownSheetProps {
  drilldownState: DrilldownState;
  onClose: () => void;
  drilldownRows: AgentHealthAssetRow[];
  drilldownTotal: number;
  drilldownPage: number;
  drilldownPageSize: number;
  drilldownSortKey: string | null;
  drilldownSortDir: "asc" | "desc";
  search: string;
  onSearchChange: (value: string) => void;
  onSearchSubmit: (value: string) => void;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onSort: (key: string) => void;
  openAssetTool: (asset: AgentHealthAssetRow, tool: string) => void;
  filterCount: number;
  onOpenFilters: () => void;
  /** The Filters drawer; rendered inside the sheet so interacting with it doesn't close the drilldown. */
  filtersSheet: ReactNode;
}

function AgentHealthDrilldownSheet({
  drilldownState,
  onClose,
  drilldownRows,
  drilldownTotal,
  drilldownPage,
  drilldownPageSize,
  drilldownSortKey,
  drilldownSortDir,
  search,
  onSearchChange,
  onSearchSubmit,
  onPageChange,
  onPageSizeChange,
  onSort,
  openAssetTool,
  filterCount,
  onOpenFilters,
  filtersSheet,
}: AgentHealthDrilldownSheetProps) {
  const isOpen = Boolean(
    drilldownState.statusDrilldown ||
    drilldownState.envDrilldown ||
    drilldownState.appDrilldown ||
    drilldownState.orgDrilldown ||
    drilldownState.matrixDrilldown,
  );
  const headerInfo = getDrilldownHeaderText(drilldownState);

  return (
    <Sheet
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="flex w-[90vw] max-w-none flex-col gap-0 border-l border-border bg-background p-0 sm:max-w-none"
        onInteractOutside={(e) => {
          if ((e.target as HTMLElement | null)?.closest("[data-modal-root]")) {
            e.preventDefault();
          }
        }}
      >
        <DrilldownSheetHeader
          title={headerInfo.title}
          slice={headerInfo.slice}
        />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="rounded-lg border border-border bg-card p-3">
            <div className="mb-3">
              <TableToolbar
                search={{
                  value: search,
                  onChange: onSearchChange,
                  onSubmit: onSearchSubmit,
                  placeholder: "Search",
                  ariaLabel: "Search drilldown assets",
                }}
                pageSize={drilldownPageSize}
                onPageSizeChange={(n) => {
                  onPageSizeChange(n);
                  onPageChange(0);
                }}
                pageSizeLabel="Rows per page"
                trailing={
                  <Button
                    type="button"
                    variant="none"
                    size="none"
                    onClick={onOpenFilters}
                    aria-haspopup="dialog"
                    className={TOOLBAR_BUTTON_CLASS}
                  >
                    <FilterIcon className="h-4 w-4" />
                    Filters
                    {filterCount > 0 && (
                      <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-semibold text-primary">
                        {filterCount}
                      </span>
                    )}
                  </Button>
                }
              />
            </div>
            <DataTable
              maxHeight="500px"
              columns={getAgentHealthAssetColumns(drilldownRows, openAssetTool)}
              data={drilldownRows}
              emptyText="No assets found"
              compact
              frameless
              page={drilldownPage}
              pageSize={drilldownPageSize}
              totalItems={drilldownTotal}
              onPageChange={onPageChange}
              itemLabel="assets"
              sortKey={drilldownSortKey}
              sortDir={drilldownSortDir}
              onSort={onSort}
            />
          </div>
        </div>
        {filtersSheet}
      </SheetContent>
    </Sheet>
  );
}

export function AgentHealth() {
  const [selectedAsset, setSelectedAsset] = useState<AgentHealthAssetRow | null>(null);
  // The clicked tool column; only this tool is requested for the asset's details.
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const openAssetTool = (asset: AgentHealthAssetRow, tool: string) => {
    setSelectedTool(tool);
    setSelectedAsset(asset);
  };
  const [toolDetailRows, setToolDetailRows] = useState<ToolDetailRow[] | null>(null);
  // The input's live value; the assets fetch below only reacts to `appliedAssetSearch`,
  // which is updated on Enter (see the search input's onKeyDown below).
  const [assetSearch, setAssetSearch] = useState("");
  const [appliedAssetSearch, setAppliedAssetSearch] = useState("");
  const [assetPage, setAssetPage] = useState(0);
  const [assetPageSize, setAssetPageSize] = useState(10);
  const [assetSortKey, setAssetSortKey] = useState<string | null>(null);
  const [assetSortDir, setAssetSortDir] = useState<"asc" | "desc">("asc");

  // Per-asset tool detail modal: nulls in the response render as "-" (not installed).
  useEffect(() => {
    if (!selectedAsset) {
      setToolDetailRows(null);
      return;
    }
    let cancelled = false;
    // appliedTool is declared further down; read here at effect time, scoped to the selected tools.
    fetchAgentHealthAssetTools(selectedAsset.id, selectedTool ? [selectedTool] : appliedTool)
      .then((rows) => {
        if (cancelled) return;
        if (!Array.isArray(rows)) {
          console.error("Agent health asset tools: expected an array, got", rows);
          setToolDetailRows([]);
          return;
        }
        setToolDetailRows(
          rows.map((row) => ({
            tool: row.tool,
            version: row.version ?? "-",
            status: row.status,
            daysSinceReporting: row.daysSinceReporting ?? "-",
            lastHeartbeat: row.lastHeartbeat ?? "-",
          })),
        );
      })
      .catch((err: unknown) => {
        console.error("Agent health asset tools fetch failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAsset, selectedTool]);

  // "Total" = all four statuses combined; null = drawer closed. Sort/page reset on every open.
  const [statusDrilldown, setStatusDrilldown] = useState<AgentStatus | "Total" | null>(null);
  // Env/app/org drilldowns yield that slice's unhealthy assets only, and are mutually
  // exclusive with the status drilldown.
  const [envDrilldown, setEnvDrilldown] = useState<string | null>(null);
  const [appDrilldown, setAppDrilldown] = useState<string | null>(null);
  const [orgDrilldown, setOrgDrilldown] = useState<string | null>(null);
  const [coverageView, setCoverageView] = useState<
    "application" | "organization" | "environment"
  >("organization");
  const [matrixDrilldown, setMatrixDrilldown] = useState<{
    application: string;
    environment: string;
    status: AgentStatus;
  } | null>(null);
  const [drilldownPage, setDrilldownPage] = useState(0);
  const [drilldownPageSize, setDrilldownPageSize] = useState(10);
  const [drilldownSortKey, setDrilldownSortKey] = useState<string | null>(null);
  const [drilldownSortDir, setDrilldownSortDir] = useState<"asc" | "desc">("asc");

  const [drilldownSearch, setDrilldownSearch] = useState("");
  const [appliedDrilldownSearch, setAppliedDrilldownSearch] = useState("");

  const resetDrilldownView = () => {
    setDrillApplied(EMPTY_DRILL_FILTERS);
    setDrillFiltersOpen(false);
    setDrilldownSearch("");
    setAppliedDrilldownSearch("");
    setDrilldownPage(0);
    setDrilldownSortKey(null);
    setDrilldownSortDir("asc");
  };

  const handleStatusDrilldown = (name: string) => {
    setStatusDrilldown(name as AgentStatus | "Total");
    setEnvDrilldown(null);
    setAppDrilldown(null);
    setOrgDrilldown(null);
    setMatrixDrilldown(null);
    resetDrilldownView();
  };

  // The clicked bar's slice follows the card's active view.
  const handleCoverageBarClick = (datum: RankedBarDatum) => {
    setStatusDrilldown(null);
    setMatrixDrilldown(null);
    setAppDrilldown(null);
    setOrgDrilldown(null);
    setEnvDrilldown(null);
    if (coverageView === "application") {
      setAppDrilldown(datum.name);
    } else if (coverageView === "organization") {
      setOrgDrilldown(datum.name);
    } else {
      setEnvDrilldown(datum.name);
    }
    resetDrilldownView();
  };

  const handleMatrixDrilldown = (entry: AgentReportingEntry) => {
    setMatrixDrilldown({
      application: entry.application,
      environment: entry.environment,
      status: ASSET_STATUS_BY_MATRIX[entry.status],
    });
    setStatusDrilldown(null);
    setEnvDrilldown(null);
    setAppDrilldown(null);
    setOrgDrilldown(null);
    resetDrilldownView();
  };

  // Same three-state sort cycle as the roster.
  const handleDrilldownSort = (key: string) => {
    setDrilldownPage(0);
    const next = cycleSort3State(drilldownSortKey, drilldownSortDir, key);
    setDrilldownSortKey(next.sortKey);
    setDrilldownSortDir(next.sortDir);
  };

  // Three-state cycle: ascending, then descending, then back to the roster's own order.
  const handleAssetSort = (key: string) => {
    setAssetPage(0);
    const next = cycleSort3State(assetSortKey, assetSortDir, key);
    setAssetSortKey(next.sortKey);
    setAssetSortDir(next.sortDir);
  };

  // Draft selections only take effect on "Filter", so switching a dropdown never re-filters
  // the roster out from under someone mid-pick.
  const scopeTree = useAccessibleOrganizationsTree();
  const [draftScope, setDraftScope] = useState<string[]>([]);
  const [appliedScope, setAppliedScope] = useState<string[]>([]);
  // The Environment dropdown was removed from the page, but the selection still flows into
  // every request's global filter; empty means "no filter, all environments".
  const [draftEnvironment, setDraftEnvironment] = useState<string[]>([]);
  const [appliedEnvironment, setAppliedEnvironment] = useState<string[]>([]);
  // Options come from POST /cmdb/tools/by-org, scoped to the organizations the
  // current user can access (the full accessible tree) -- same source as the
  // Asset Coverage page's Tool filter.
  const {
    tools,
    draftTool, setDraftTool,
    appliedTool, setAppliedTool,
    toolsSeeded,
  } = useCmdbToolOptions(scopeTree);

  // Seeds Application with every node once the tree resolves; guarded so it runs once and
  // never clobbers a selection the user already changed before a slow fetch settles.
  const [scopeSeeded, setScopeSeeded] = useState(false);
  useEffect(() => {
    if (scopeSeeded || scopeTree.loading) return;
    if (scopeTree.data.length > 0) {
      const allIds = collectAllScopeIds(scopeTree.data);
      setDraftScope(allIds);
      setAppliedScope(allIds);
    }
    setScopeSeeded(true);
  }, [scopeSeeded, scopeTree.loading, scopeTree.data]);

  // Waits for both seeds so the page fires exactly one request per endpoint on load.
  const filtersReady = scopeSeeded && toolsSeeded;

  // Bumped on every Filter/Clear click so the fetch effects below always re-run, even when the
  // applied selection ends up identical to what it already was (e.g. re-clicking Filter with no
  // changes) -- clicking the button should always call the APIs, not be deduped away.
  const [filterTrigger, setFilterTrigger] = useState(0);

  // More Filters sheet (above the roster): Environment feeds the global filter; Type and Agent
  // Status only narrow the roster table.
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [filterOptions, setFilterOptions] = useState<AgentHealthFilterOptions>({
    environments: [],
    types: [],
    agentStatuses: [],
  });
  const [draftType, setDraftType] = useState<string[]>([]);
  const [appliedType, setAppliedType] = useState<string[]>([]);
  const [draftAgentStatus, setDraftAgentStatus] = useState<string[]>([]);
  const [appliedAgentStatus, setAppliedAgentStatus] = useState<string[]>([]);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);
  // Table-only Application / Tool filters (empty = no restriction). Application options come from
  // the Scan Coverage overview filter-options API; Tool options are the page's own tool list.
  const [applicationOptions, setApplicationOptions] = useState<string[]>([]);
  const [draftTableApps, setDraftTableApps] = useState<string[]>([]);
  const [appliedTableApps, setAppliedTableApps] = useState<string[]>([]);
  const [draftTableTools, setDraftTableTools] = useState<string[]>([]);
  const [appliedTableTools, setAppliedTableTools] = useState<string[]>([]);
  const tableAppsKey = appliedTableApps.join(",");
  const tableToolsKey = appliedTableTools.join(",");
  const typeKey = appliedType.join(",");
  const agentStatusKey = appliedAgentStatus.join(",");
  const moreFiltersCount =
    (appliedEnvironment.length > 0 ? 1 : 0) +
    (appliedType.length > 0 ? 1 : 0) +
    (appliedAgentStatus.length > 0 ? 1 : 0) +
    (appliedTableApps.length > 0 && appliedTableApps.length < applicationOptions.length ? 1 : 0) +
    (appliedTableTools.length > 0 ? 1 : 0);

  const selectAllDrafts = (opts: AgentHealthFilterOptions) => {
    setDraftEnvironment(appliedEnvironment.length > 0 ? appliedEnvironment : opts.environments);
    setDraftType(appliedType.length > 0 ? appliedType : opts.types);
    setDraftAgentStatus(appliedAgentStatus.length > 0 ? appliedAgentStatus : opts.agentStatuses);
  };

  // Drilldown sheet filters: same dropdowns as the table's, but scoped to the open drilldown.
  // Empty applied list = untouched (page-level selection applies).
  const [drillFiltersOpen, setDrillFiltersOpen] = useState(false);
  const [drillApplied, setDrillApplied] = useState<DrilldownFilters>(EMPTY_DRILL_FILTERS);
  const [drillDraft, setDrillDraft] = useState<DrilldownFilters>(EMPTY_DRILL_FILTERS);
  const drillAppliedKey = Object.values(drillApplied)
    .map((v) => v.join(","))
    .join("|");

  const openDrilldownFilters = () => {
    const seed = (apps: string[], opts: AgentHealthFilterOptions) =>
      setDrillDraft({
        applications: drillApplied.applications.length > 0 ? drillApplied.applications : apps,
        tools: drillApplied.tools.length > 0 ? drillApplied.tools : tools,
        environments: drillApplied.environments.length > 0 ? drillApplied.environments : opts.environments,
        types: drillApplied.types.length > 0 ? drillApplied.types : opts.types,
        agentStatuses: drillApplied.agentStatuses.length > 0 ? drillApplied.agentStatuses : opts.agentStatuses,
      });
    seed(applicationOptions, filterOptions);
    setDrillFiltersOpen(true);
    fetchScanDashboardFilterOptions()
      .then((appOpts) => {
        const names = Array.from(new Set((appOpts?.applications ?? []).map((a) => a.name))).sort((a, b) =>
          a.localeCompare(b),
        );
        setApplicationOptions(names);
        fetchAgentHealthFilterOptions()
          .then((opts) => {
            const next: AgentHealthFilterOptions = {
              environments: opts?.environments ?? [],
              types: opts?.types ?? [],
              agentStatuses: opts?.agentStatuses ?? [],
            };
            setFilterOptions(next);
            seed(names, next);
          })
          .catch((err: unknown) => console.error("Agent health filter options fetch failed:", err));
      })
      .catch((err: unknown) => console.error("Agent health application options fetch failed:", err));
  };
  const applyDrilldownFilters = () => {
    setDrillApplied({
      // Kept as chosen even when all are checked, so apps outside the page-level scope still go out.
      applications: drillDraft.applications,
      tools: unlessAll(drillDraft.tools, tools),
      environments: unlessAll(drillDraft.environments, filterOptions.environments),
      types: unlessAll(drillDraft.types, filterOptions.types),
      agentStatuses: unlessAll(drillDraft.agentStatuses, filterOptions.agentStatuses),
    });
    setDrilldownPage(0);
    setDrillFiltersOpen(false);
  };
  const clearAndCloseDrilldownFilters = () => {
    setDrillApplied(EMPTY_DRILL_FILTERS);
    setDrilldownPage(0);
    setDrillFiltersOpen(false);
  };

  const handleResetMoreFilters = () => {
    setDraftEnvironment(filterOptions.environments);
    setDraftType(filterOptions.types);
    setDraftAgentStatus(filterOptions.agentStatuses);
    setDraftTableApps(applicationOptions);
    setDraftTableTools(tools);
  };

  const openMoreFilters = () => {
    setDraftEnvironment(appliedEnvironment);
    setDraftType(appliedType);
    setDraftAgentStatus(appliedAgentStatus);
    setDraftTableApps(appliedTableApps.length > 0 ? appliedTableApps : applicationOptions);
    setDraftTableTools(appliedTableTools.length > 0 ? appliedTableTools : tools);
    setMoreFiltersOpen(true);
    fetchScanDashboardFilterOptions()
      .then((opts) => {
        const names = Array.from(new Set((opts?.applications ?? []).map((a) => a.name))).sort((a, b) =>
          a.localeCompare(b),
        );
        setApplicationOptions(names);
        setDraftTableApps(appliedTableApps.length > 0 ? appliedTableApps : names);
      })
      .catch((err: unknown) => console.error("Agent health application options fetch failed:", err));
    // Fetched on every open so the options are always current.
    setFilterOptionsLoading(true);
    fetchAgentHealthFilterOptions()
      .then((opts) => {
        const next: AgentHealthFilterOptions = {
          environments: opts?.environments ?? [],
          types: opts?.types ?? [],
          agentStatuses: opts?.agentStatuses ?? [],
        };
        setFilterOptions(next);
        selectAllDrafts(next);
      })
      .catch((err: unknown) => console.error("Agent health filter options fetch failed:", err))
      .finally(() => setFilterOptionsLoading(false));
  };
  const applyMoreFilters = () => {
    setAppliedEnvironment(unlessAll(draftEnvironment, filterOptions.environments));
    setAppliedType(unlessAll(draftType, filterOptions.types));
    setAppliedAgentStatus(unlessAll(draftAgentStatus, filterOptions.agentStatuses));
    // Keep an all-selected list: collapsing it to [] fell back to the page-level scope names,
    // which drop apps missing from the scope tree (e.g. Canara Bank).
    setAppliedTableApps(draftTableApps);
    setAppliedTableTools(unlessAll(draftTableTools, tools));
    setAssetPage(0);
    setFilterTrigger((v) => v + 1);
    setMoreFiltersOpen(false);
  };
  const clearAndCloseMoreFilters = () => {
    setDraftEnvironment(filterOptions.environments);
    setDraftType(filterOptions.types);
    setDraftAgentStatus(filterOptions.agentStatuses);
    setDraftTableApps(applicationOptions);
    setDraftTableTools(tools);
    setAppliedTableApps([]);
    setAppliedTableTools([]);
    setAppliedEnvironment([]);
    setAppliedType([]);
    setAppliedAgentStatus([]);
    setAssetPage(0);
    setFilterTrigger((v) => v + 1);
    setMoreFiltersOpen(false);
  };

  const appliedApplicationNames = useMemo(
    () => selectedScopeApplicationNames(scopeTree.data, appliedScope),
    [scopeTree.data, appliedScope],
  );
  // Forward exactly what's checked in the Application scope filter -- collapsing a full
  // selection down to "[]" made the API's own reading of an empty array ("no filter" vs
  // "match nothing") the one that decided what came back, not the selection actually applied.
  const applicationNamesFilter = appliedApplicationNames;
  // Joined string keys (not the arrays) so effects only refire when selection content actually
  // changes, not on every fresh array reference from useMemo.
  const applicationNamesKey = applicationNamesFilter.join(",");
  const environmentKey = appliedEnvironment.join(",");
  const toolKey = appliedTool.join(",");

  // Backs the freshness banner, activity board, status donut, and coverage matrix; each keeps
  // its prior state until its own response lands, so no tile goes blank while loading.
  const [freshnessSummary, setFreshnessSummary] = useState<AgentHealthFreshnessSummary | null>(
    null,
  );
  const [freshnessItems, setFreshnessItems] = useState<FreshnessItem[] | null>(null);
  const [agentSummary, setAgentSummary] = useState<AgentHealthSummary | null>(null);
  const [statusMatrixRows, setStatusMatrixRows] = useState<AgentHealthStatusMatrixRow[] | null>(
    null,
  );
  // Dedupe guard: skips a re-fetch when the effect reruns against an unchanged filter key.
  const freshnessFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!filtersReady) return;
    const key = `${applicationNamesKey}::${toolKey}::${filterTrigger}`;
    if (freshnessFetchKeyRef.current === key) return;
    freshnessFetchKeyRef.current = key;
    let cancelled = false;
    const filter = {
      applicationNames: applicationNamesFilter,
      environments: [], // Drawer Environment applies to the roster table only.
      tools: appliedTool,
    };
    fetchAgentHealthFreshnessSummary(filter)
      .then((summary) => {
        if (!cancelled) setFreshnessSummary(summary);
      })
      .catch((err: unknown) => {
        console.error("Agent health freshness summary fetch failed:", err);
      });
    fetchAgentHealthFreshnessList(filter)
      .then((list) => {
        if (cancelled) return;
        // Guards against an enveloped response (e.g. {data: [...]}), same as useAccessibleOrganizationsTree.
        if (!Array.isArray(list)) {
          console.error("Agent health freshness list: expected an array, got", list);
          return;
        }
        setFreshnessItems(
          list.map((item) => ({
            tool: item.tool,
            status: item.status,
            lastSuccessful: item.lastSuccessful,
            expected: item.expected ?? undefined,
            latestAttempt: item.latestAttempt ?? undefined,
            note: item.note ?? undefined,
            recencyMinutes: item.recencyMinutes,
          })),
        );
      })
      .catch((err: unknown) => {
        console.error("Agent health freshness list fetch failed:", err);
      });
    fetchAgentHealthSummary(filter)
      .then((summary) => {
        if (!cancelled) setAgentSummary(summary);
      })
      .catch((err: unknown) => {
        console.error("Agent health summary fetch failed:", err);
      });
    fetchAgentHealthStatusMatrix(filter)
      .then((rows) => {
        if (cancelled) return;
        if (!Array.isArray(rows)) {
          console.error("Agent health status matrix: expected an array, got", rows);
          return;
        }
        setStatusMatrixRows(rows);
      })
      .catch((err: unknown) => {
        console.error("Agent health status matrix fetch failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [filtersReady, applicationNamesKey, toolKey, filterTrigger]);

  const freshnessSlices = buildFreshnessSlices(
    freshnessSummary
      ? {
        Healthy: freshnessSummary.healthy,
        Stale: freshnessSummary.stale,
        Failed: freshnessSummary.failed,
        Unknown: freshnessSummary.unknown,
      }
      : {},
  );

  const statusSlices = useMemo<StatusSliceItem[]>(() => {
    const counts: Record<AgentStatus, number> = agentSummary
      ? {
        Active: agentSummary.active,
        Inactive: agentSummary.inactive,
        Unreachable: agentSummary.unreachable,
        "Never Reported": agentSummary.neverReported,
      }
      : {
        Active: 0,
        Inactive: 0,
        Unreachable: 0,
        "Never Reported": 0,
      };
    // All four statuses pass through with zero counts included, so the legend keeps a swatch
    // for every status; DonutPieChart itself drops 0-value slices from the ring.
    return (["Active", "Inactive", "Unreachable", "Never Reported"] as AgentStatus[]).map(
      (name) => ({
        name,
        value: counts[name],
        color: STATUS_COLORS[name],
      }),
    );
  }, [agentSummary]);

  const [coverageRows, setCoverageRows] = useState<AgentHealthCoverageRow[] | null>(null);
  const coverageFetchKeyRef = useRef<string | null>(null);
  // Organization view only makes sense with more than one organization
  const [orgCount, setOrgCount] = useState<number | null>(null);
  useEffect(() => {
    if (!filtersReady) return;
    const key = `${applicationNamesKey}::${toolKey}::${coverageView}::${filterTrigger}`;
    if (coverageFetchKeyRef.current === key) return;
    coverageFetchKeyRef.current = key;
    let cancelled = false;
    fetchAgentHealthCoverage({
      applicationNames: applicationNamesFilter,
      environments: [],
      tools: appliedTool,
      groupBy: coverageView,
    })
      .then((rows) => {
        if (cancelled) return;
        if (!Array.isArray(rows)) {
          console.error("Agent health coverage: expected an array, got", rows);
          return;
        }
        if (coverageView === "organization") {
          setOrgCount(rows.length);
          if (rows.length === 1) {
            setCoverageView("application");
            return;
          }
        }
        setCoverageRows(rows);
      })
      .catch((err: unknown) => {
        console.error("Agent health coverage fetch failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [filtersReady, applicationNamesKey, toolKey, coverageView, filterTrigger]);

  // Reshaped for RankedBarChart: a single "Unhealthy" measure per category, ranked highest first.
  const coverageRanked = useMemo<RankedBarDatum[]>(
    () =>
      (coverageRows ?? []).map((row) => ({
        name: row.name,
        value: row.unhealthy,
        color: HEX.critical,
      })),
    [coverageRows],
  );

  const agentReportingData = statusMatrixRows ?? [];

  const toolDraftDirty = draftTool.length > 0;
  const toolFilterActive = appliedTool.length > 0;
  const draftDirty =
    draftScope.length > 0 || draftEnvironment.length > 0 || toolDraftDirty;
  const filtersActive =
    appliedScope.length > 0 || appliedEnvironment.length > 0 || toolFilterActive;

  // Filter is a no-op until the selection differs from what is already applied.
  const filtersUnchanged =
    sameSelection(draftScope, appliedScope) && sameSelection(draftTool, appliedTool);

  const handleApplyFilters = () => {
    setAppliedScope(draftScope);
    setAppliedEnvironment(unlessAll(draftEnvironment, filterOptions.environments));
    setAppliedTool(draftTool);
    // Mirror the page-level Application/Tool selection into the table filters drawer.
    setAppliedTableApps(selectedScopeApplicationNames(scopeTree.data, draftScope));
    setAppliedTableTools(draftTool);
    setAssetPage(0);
    setFilterTrigger((v) => v + 1);
  };

  // Clear empties the selections and applies them; disabled once that is already the case.
  const filtersCleared =
    draftScope.length === 0 &&
    draftEnvironment.length === 0 &&
    draftTool.length === 0 &&
    appliedScope.length === 0 &&
    appliedEnvironment.length === 0 &&
    appliedTool.length === 0;

  const handleClearFilters = () => {
    setDraftScope([]);
    setDraftEnvironment([]);
    setDraftTool([]);
    setAppliedScope([]);
    setAppliedEnvironment([]);
    setAppliedTool([]);
    setAppliedTableApps([]);
    setAppliedTableTools([]);
    setDraftTableApps(applicationOptions);
    setDraftTableTools(tools);
    setAssetPage(0);
    setFilterTrigger((v) => v + 1);
  };



  // Server-side search/sort/paging via the same endpoint every drilldown sheet uses below,
  // just without their scope fields.
  const [assetRows, setAssetRows] = useState<AgentHealthAssetRow[]>([]);
  const [assetTotal, setAssetTotal] = useState(0);
  const assetsFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!filtersReady) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      const key = [
        applicationNamesKey,
        environmentKey,
        toolKey,
        typeKey,
        agentStatusKey,
        tableAppsKey,
        tableToolsKey,
        appliedAssetSearch,
        assetPage,
        assetPageSize,
        assetSortKey,
        assetSortKey ? assetSortDir : "",
        filterTrigger,
      ].join("::");
      if (assetsFetchKeyRef.current === key) return;
      assetsFetchKeyRef.current = key;
      fetchAgentHealthAssets({
        applicationNames: narrow(applicationNamesFilter, appliedTableApps),
        environments: appliedEnvironment,
        tools: narrow(appliedTool, appliedTableTools),
        types: appliedType,
        agentStatuses: appliedAgentStatus,
        search: appliedAssetSearch || undefined,
        page: assetPage,
        pageSize: assetPageSize,
        ...getAgentHealthSortPayload(assetSortKey, assetSortDir),
      })
        .then((res) => {
          if (cancelled) return;
          if (!Array.isArray(res?.items)) {
            console.error("Agent health assets: expected { items: [...] }, got", res);
            return;
          }
          setAssetRows(res.items);
          setAssetTotal(res.total);
        })
        .catch((err: unknown) => {
          console.error("Agent health assets fetch failed:", err);
        });
    }, ASSETS_FETCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    filtersReady,
    applicationNamesKey,
    environmentKey,
    toolKey,
    typeKey,
    agentStatusKey,
    tableAppsKey,
    tableToolsKey,
    appliedAssetSearch,
    assetPage,
    assetPageSize,
    assetSortKey,
    assetSortDir,
    filterTrigger,
  ]);

  // Same assets endpoint, scoped by whichever drilldown is open; skips the fetch when none is.
  const [drilldownRows, setDrilldownRows] = useState<AgentHealthAssetRow[]>([]);
  const [drilldownTotal, setDrilldownTotal] = useState(0);
  const drilldownFetchKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!filtersReady) return;
    const scope = resolveDrilldownScope({
      statusDrilldown,
      appDrilldown,
      orgDrilldown,
      envDrilldown,
      matrixDrilldown,
    });
    if (!scope) return;
    const key = [
      JSON.stringify(scope),
      applicationNamesKey,
      toolKey,
      drillAppliedKey,
      drilldownPage,
      drilldownPageSize,
      drilldownSortKey,
      drilldownSortKey ? drilldownSortDir : "",
      appliedDrilldownSearch,
      filterTrigger,
    ].join("::");
    if (drilldownFetchKeyRef.current === key) return;
    drilldownFetchKeyRef.current = key;
    let cancelled = false;
    fetchAgentHealthAssets({
      applicationNames: drillApplied.applications.length > 0 ? drillApplied.applications : applicationNamesFilter,
      environments: drillApplied.environments,
      tools: drillApplied.tools.length > 0 ? drillApplied.tools : appliedTool,
      types: drillApplied.types,
      agentStatuses: drillApplied.agentStatuses,
      ...scope,
      search: appliedDrilldownSearch || undefined,
      page: drilldownPage,
      pageSize: drilldownPageSize,
      ...getAgentHealthSortPayload(drilldownSortKey, drilldownSortDir),
    })
      .then((res) => {
        if (cancelled) return;
        if (!Array.isArray(res?.items)) {
          console.error("Agent health drilldown assets: expected { items: [...] }, got", res);
          return;
        }
        setDrilldownRows(res.items);
        setDrilldownTotal(res.total);
      })
      .catch((err: unknown) => {
        console.error("Agent health drilldown assets fetch failed:", err);
      });
    return () => {
      cancelled = true;
    };
  }, [
    filtersReady,
    statusDrilldown,
    appDrilldown,
    orgDrilldown,
    envDrilldown,
    matrixDrilldown,
    applicationNamesKey,
    toolKey,
    drillAppliedKey,
    drilldownPage,
    drilldownPageSize,
    drilldownSortKey,
    drilldownSortDir,
    appliedDrilldownSearch,
    filterTrigger,
  ]);

  const drilldownState = useMemo<DrilldownState>(
    () => ({
      statusDrilldown,
      appDrilldown,
      orgDrilldown,
      envDrilldown,
      matrixDrilldown,
    }),
    [statusDrilldown, appDrilldown, orgDrilldown, envDrilldown, matrixDrilldown],
  );

  const drilldownHiddenFilters = getDrilldownHiddenFilters(drilldownState);

  const closeDrilldown = () => {
    setStatusDrilldown(null);
    setEnvDrilldown(null);
    setAppDrilldown(null);
    setOrgDrilldown(null);
    setMatrixDrilldown(null);
  };

  const coverageViewLabel = getCoverageViewLabel(coverageView);

  return (
    <PageShell className="space-y-5">
      <header className="border-b border-border pb-4">
        <h1 className="text-xl font-bold leading-tight tracking-tight text-foreground">
          Agent Health
        </h1>
        <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
          Monitor agent deployment, reporting health, and version compliance across assets.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2">
        <ScopeTreeFilter
          data={scopeTree.data}
          value={draftScope}
          onChange={setDraftScope}
          loading={scopeTree.loading}
          variant="pill"
          triggerLabel={scopeTriggerLabel(scopeTree.data, draftScope, "Application")}
        />
        <FilterDropdown
          label="Tool"
          options={tools}
          value={draftTool}
          onChange={setDraftTool}
          searchPlaceholder="Search"
          variant="pill"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleApplyFilters}
          disabled={filtersUnchanged}
          aria-label="Apply filters"
        >
          Filter
        </Button>
        {(filtersActive || draftDirty) && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClearFilters}
            disabled={filtersCleared}
            aria-label="Clear filters"
          >
            Clear
          </Button>
        )}
      </div>

      {/* One grid with equal-height rows so row 2 matches the height row 1 takes. */}
      <div className="grid grid-cols-12 gap-4 lg:auto-rows-fr">
        <div className="col-span-12 flex lg:col-span-6">
          <DataFreshnessCard slices={freshnessSlices} items={freshnessItems ?? []} />
        </div>

        <ChartCard
          title="Agent Status Breakdown"
          divider
          className={`col-span-12 lg:col-span-6 h-full ${TILE_CLASS}`}
        >
          <DonutPieChart
            {...AGENT_STATUS_DONUT_PROPS}
            name="agent-status-breakdown"
            data={statusSlices}
            onSliceClick={(slice) => handleStatusDrilldown(slice.label)}
            emptyMessage="No agents match the selected filters"
            zeroValuesMessage="No agents match the selected filters"
            ariaLabel="Agent status breakdown"
          />
        </ChartCard>

        <ChartCard
          title="Agent Coverage by Application & Environment"
          divider
          className={`col-span-12 lg:col-span-6 ${TILE_CLASS}`}
          actions={
            // Invisible twin of the neighbouring card's SegmentedTabs so both cards' titles
            // and dividers line up.
            <div className="invisible" aria-hidden="true">
              <SegmentedTabs
                items={[{ id: "spacer", label: "Organization", icon: Landmark }]}
                value="spacer"
                onChange={() => { }}
                ariaLabel="spacer"
                size="sm"
                responsiveLabels
              />
            </div>
          }
        >
          {/* Absolute inner box keeps the chart out of the row height calculation, so the row
              stays as tall as row 1 and the chart scrolls inside it. */}
          <div className="relative min-h-[320px] flex-1 lg:min-h-0">
            <div className="flex flex-col lg:absolute lg:inset-0">
              <AgentReportingMatrix
                data={agentReportingData}
                id="agent-reporting-timeline"
                name="agent-reporting-timeline"
                rowHeight={48}
                markRadius={8}
                maxHeight={1000}
                interactiveLegend
                onCellClick={handleMatrixDrilldown}
                xAxisLabel="Environment"
                yAxisLabel="Application"
              />
            </div>
          </div>
        </ChartCard>

        <ChartCard
          title={`Unhealthy Agents by ${coverageViewLabel}`}
          divider
          className={`col-span-12 lg:col-span-6 ${TILE_CLASS}`}
          actions={
            <SegmentedTabs
              items={[
                ...(orgCount === 1
                  ? []
                  : [{ id: "organization", label: "Organization", icon: Landmark }]),
                { id: "application", label: "Application", icon: Package },
                { id: "environment", label: "Environment", icon: Globe },
              ]}
              value={coverageView}
              onChange={(id) => {
                setCoverageView(
                  id === "organization"
                    ? "organization"
                    : id === "environment"
                      ? "environment"
                      : "application",
                );
                // A slice from another view no longer matches the bars.
                setAppDrilldown(null);
                setOrgDrilldown(null);
                setEnvDrilldown(null);
              }}
              ariaLabel="Unhealthy agents view"
              size="sm"
              responsiveLabels
            />
          }
        >
          <div className="relative min-h-[320px] flex-1 lg:min-h-0">
            <div className="flex flex-col lg:absolute lg:inset-0">
              <RankedBarChart
                data={coverageRanked}
                height={AGENT_STATUS_DONUT_PROPS.height + 40}
                stretch
                valueLabel="Unhealthy"
                scrollable
                rowGap={20}
                categoryTickClassName="fill-[#334155] dark:fill-[#e2e8f0]"
                onBarClick={handleCoverageBarClick}
                ariaLabel={`Unhealthy agents by ${coverageView}`}
                emptyMessage={
                  (coverageRows?.length ?? 0) > 0
                    ? "All agents in the selected scope are healthy"
                    : "No agents match the selected filters"
                }
                xAxisLabel="Unhealthy"
                yAxisLabel={coverageViewLabel}
              />
            </div>
          </div>
        </ChartCard>
      </div>

      <section>
        <div className="mb-3">
          <TableToolbar
            search={{
              value: assetSearch,
              onChange: setAssetSearch,
              onSubmit: (value) => {
                setAppliedAssetSearch(value);
                setAssetPage(0);
              },
              placeholder: "Search",
              ariaLabel: "Search assets",
            }}
            pageSize={assetPageSize}
            onPageSizeChange={(n) => {
              setAssetPageSize(n);
              setAssetPage(0);
            }}
            pageSizeLabel="Rows per page"
            trailing={
              <Button
                type="button"
                variant="none"
                size="none"
                onClick={openMoreFilters}
                aria-haspopup="dialog"
                aria-expanded={moreFiltersOpen}
                className={TOOLBAR_BUTTON_CLASS}
              >
                <FilterIcon className="h-4 w-4" />
                Filters
                {moreFiltersCount > 0 && (
                  <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-xs font-semibold text-primary">
                    {moreFiltersCount}
                  </span>
                )}
              </Button>
            }
          />
        </div>
        <DataTable
          maxHeight="500px"
          columns={getAgentHealthAssetColumns(assetRows, openAssetTool)}
          data={assetRows}
          emptyText="No assets found"
          compact
          frameless
          page={assetPage}
          pageSize={assetPageSize}
          totalItems={assetTotal}
          onPageChange={setAssetPage}
          itemLabel="assets"
          sortKey={assetSortKey}
          sortDir={assetSortDir}
          onSort={handleAssetSort}
        />
      </section>

      <AgentHealthMoreFiltersSheet
        open={moreFiltersOpen}
        onOpenChange={(next) => !next && setMoreFiltersOpen(false)}
        filterOptions={filterOptions}
        filterOptionsLoading={filterOptionsLoading}
        applicationOptions={applicationOptions}
        tools={tools}
        draftEnvironment={draftEnvironment}
        setDraftEnvironment={setDraftEnvironment}
        draftType={draftType}
        setDraftType={setDraftType}
        draftAgentStatus={draftAgentStatus}
        setDraftAgentStatus={setDraftAgentStatus}
        draftTableApps={draftTableApps}
        setDraftTableApps={setDraftTableApps}
        draftTableTools={draftTableTools}
        setDraftTableTools={setDraftTableTools}
        onReset={handleResetMoreFilters}
        onClearAndClose={clearAndCloseMoreFilters}
        onApply={applyMoreFilters}
      />

      <Modal
        open={selectedAsset !== null}
        onClose={() => setSelectedAsset(null)}
        title={selectedAsset?.assetName ?? ""}
        wide
      >
        {selectedAsset && (
          <DataTable
            maxHeight="500px"
            columns={TOOL_DETAIL_COLUMNS}
            data={toolDetailRows ?? []}
            emptyText="No tools tracked"
            compact
            frameless
          />
        )}
      </Modal>

      <AgentHealthDrilldownSheet
        drilldownState={drilldownState}
        onClose={closeDrilldown}
        drilldownRows={drilldownRows}
        drilldownTotal={drilldownTotal}
        drilldownPage={drilldownPage}
        drilldownPageSize={drilldownPageSize}
        drilldownSortKey={drilldownSortKey}
        drilldownSortDir={drilldownSortDir}
        search={drilldownSearch}
        onSearchChange={setDrilldownSearch}
        onSearchSubmit={(value) => {
          setAppliedDrilldownSearch(value.trim());
          setDrilldownPage(0);
        }}
        onPageChange={setDrilldownPage}
        onPageSizeChange={setDrilldownPageSize}
        onSort={handleDrilldownSort}
        openAssetTool={openAssetTool}
        filterCount={
          (drilldownHiddenFilters.includes("environment") ? 0 : drillApplied.environments.length > 0 ? 1 : 0) +
          (drilldownHiddenFilters.includes("agentStatus") ? 0 : drillApplied.agentStatuses.length > 0 ? 1 : 0) +
          (drillApplied.types.length > 0 ? 1 : 0) +
          (drillApplied.tools.length > 0 ? 1 : 0) +
          (!drilldownHiddenFilters.includes("application") &&
          drillApplied.applications.length > 0 &&
          drillApplied.applications.length < applicationOptions.length
            ? 1
            : 0)
        }
        onOpenFilters={openDrilldownFilters}
        filtersSheet={
          <AgentHealthMoreFiltersSheet
            open={drillFiltersOpen}
            onOpenChange={(next) => !next && setDrillFiltersOpen(false)}
            filterOptions={filterOptions}
            filterOptionsLoading={false}
            applicationOptions={applicationOptions}
            tools={tools}
            draftEnvironment={drillDraft.environments}
            setDraftEnvironment={(v) => setDrillDraft((d) => ({ ...d, environments: v }))}
            draftType={drillDraft.types}
            setDraftType={(v) => setDrillDraft((d) => ({ ...d, types: v }))}
            draftAgentStatus={drillDraft.agentStatuses}
            setDraftAgentStatus={(v) => setDrillDraft((d) => ({ ...d, agentStatuses: v }))}
            draftTableApps={drillDraft.applications}
            setDraftTableApps={(v) => setDrillDraft((d) => ({ ...d, applications: v }))}
            draftTableTools={drillDraft.tools}
            setDraftTableTools={(v) => setDrillDraft((d) => ({ ...d, tools: v }))}
            onReset={() =>
              setDrillDraft({
                applications: applicationOptions,
                tools,
                environments: filterOptions.environments,
                types: filterOptions.types,
                agentStatuses: filterOptions.agentStatuses,
              })
            }
            onClearAndClose={clearAndCloseDrilldownFilters}
            onApply={applyDrilldownFilters}
            hide={drilldownHiddenFilters}
          />
        }
      />
    </PageShell>
  );
}
